import { useMemo, useState } from 'react';

import { ClipboardList, Plus } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import { GatingBanner } from '@/modulos/portal-credenciado/comum/GatingBanner';
import { CopyableText } from '@/ui/CopyableText';
import { DataTable } from '@/ui/DataTable';
import { DateRangeFilter } from '@/ui/DateRangeFilter';
import { ErrorState } from '@/ui/ErrorState';
import { FilterBar, type FilterFieldConfig } from '@/ui/FilterBar';
import { PageHeader } from '@/ui/PageHeader';
import { StatusBadge } from '@/ui/StatusBadge';
import { Button } from '@/ui/base/button';
import type { TableViewColumn } from '@/ui/table-view/tabelaVisoes';
import { useTableViewPreferences } from '@/ui/table-view/useTableViewPreferences';

import {
  STATUS_DA_SOLICITACAO,
  STATUS_PARA_SELO,
  TIPOS_DE_SOLICITACAO,
  rotuloDoTipo,
} from './contrato';
import { useSolicitacoes } from './dados';
import type { FiltroDeSolicitacoes, SolicitacaoDeServico } from './contrato';

const TAMANHO_DA_PAGINA = 10;

/**
 * Módulo, e não dentro da tela: `useTableViewPreferences` pede lista estável.
 * O protocolo identifica a linha e não se oculta; Tipo, Aberta em e Status
 * explicam os filtros de mesmo assunto, e o botão de personalizar avisa se um
 * deles estiver oculto com o filtro ativo.
 */
const COLUNAS: TableViewColumn<SolicitacaoDeServico>[] = [
  {
    id: 'protocolo',
    header: 'Protocolo',
    // O protocolo é o Link para o detalhe, e a cópia é só o botão
    // (`display=""`): o botão de copiar é um controle focável dentro da linha,
    // então a linha não pode ser também um botão (`nested-interactive`,
    // regras de interface 5 e 9). O teclado abre o detalhe pelo Link; o mouse
    // continua abrindo pela linha inteira.
    cell: (item) => (
      <span className="inline-flex items-center gap-1.5">
        <Link
          to={`/credenciado/solicitacoes/${item.id}`}
          className="focus-visible:ring-ring rounded font-mono text-sm tabular-nums hover:underline focus:outline-hidden focus-visible:ring-2"
        >
          {item.protocolo}
        </Link>
        <CopyableText
          value={item.protocolo}
          display=""
          label={`Copiar protocolo ${item.protocolo}`}
        />
      </span>
    ),
    sortKey: 'protocolo',
    isCritical: true,
  },
  {
    id: 'tipo',
    header: 'Tipo',
    cell: (item) => rotuloDoTipo(item.tipo),
    sortKey: (item) => rotuloDoTipo(item.tipo),
    linkedFilterKeys: ['tipo'],
  },
  {
    id: 'motivo',
    header: 'Motivo',
    cell: (item) => (
      <span className="line-clamp-1 text-sm text-muted-foreground">{item.motivo}</span>
    ),
  },
  {
    id: 'aberta_em',
    header: 'Aberta em',
    cell: (item) => (
      <span className="text-sm text-muted-foreground">
        {new Date(item.aberta_em).toLocaleDateString('pt-BR')}
      </span>
    ),
    sortKey: (item) => new Date(item.aberta_em).getTime(),
    linkedFilterKeys: ['periodo'],
  },
  {
    id: 'status',
    header: 'Status',
    cell: (item) => <StatusBadge status={STATUS_PARA_SELO[item.status]} label={item.status} />,
    sortKey: 'status',
    isStatusColumn: true,
    linkedFilterKeys: ['status'],
  },
];

/**
 * `/credenciado/solicitacoes` — pedidos de serviço abertos pelo credenciado
 * junto ao DTP (substituição de veículo, condutor ou monitor, alteração
 * cadastral, ou outro assunto).
 *
 * Dado mockado — ver `dados.ts` e `comum/mock.ts` para a justificativa.
 */
export function SolicitacoesPage() {
  const navigate = useNavigate();

  const [pagina, setPagina] = useState(1);
  const [filtros, setFiltros] = useState<Record<string, string>>({});
  const [periodo, setPeriodo] = useState({ from: '', to: '' });

  const filtro = useMemo<FiltroDeSolicitacoes>(
    () => ({
      pagina,
      tamanho: TAMANHO_DA_PAGINA,
      busca: filtros.busca || undefined,
      tipo: (filtros.tipo || undefined) as FiltroDeSolicitacoes['tipo'],
      status: (filtros.status || undefined) as FiltroDeSolicitacoes['status'],
      criadaDe: periodo.from || undefined,
      criadaAte: periodo.to || undefined,
    }),
    [pagina, filtros.busca, filtros.tipo, filtros.status, periodo.from, periodo.to],
  );

  const { data, isLoading, isError, refetch } = useSolicitacoes(filtro);

  const temFiltro = Boolean(
    filtros.busca || filtros.tipo || filtros.status || periodo.from || periodo.to,
  );

  const campos: FilterFieldConfig[] = [
    {
      key: 'busca',
      label: 'Busca',
      type: 'search',
      placeholder: 'Buscar por protocolo ou motivo…',
      ariaLabel: 'Buscar solicitações',
      flex: true,
    },
    {
      key: 'tipo',
      label: 'Tipo',
      type: 'select',
      options: TIPOS_DE_SOLICITACAO.map((opcao) => ({ value: opcao.value, label: opcao.label })),
      width: 'w-full sm:w-[220px]',
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: STATUS_DA_SOLICITACAO.map((opcao) => ({ value: opcao.value, label: opcao.label })),
      width: 'w-full sm:w-[160px]',
    },
  ];

  const filterValues = useMemo(
    () => ({
      tipo: filtros.tipo ?? '',
      status: filtros.status ?? '',
      periodo: periodo.from || periodo.to ? 'ativo' : '',
    }),
    [filtros.tipo, filtros.status, periodo.from, periodo.to],
  );

  const tableView = useTableViewPreferences({
    contextKey: 'credenciado:solicitacoes:list',
    columns: COLUNAS,
    filterValues,
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title="Solicitações"
        description="Acompanhe os pedidos de serviço abertos junto ao DTP — substituição de veículo, condutor ou monitor, e outros assuntos."
      >
        {/* Abaixo de 640 px o rótulo some (`hidden sm:inline`) e o botão fica
            só com o ícone: o `aria-label` mantém o nome (regra de interface 5). */}
        <Button
          onClick={() => navigate('/credenciado/solicitacoes/nova')}
          aria-label="Nova solicitação"
        >
          <Plus className="h-4 w-4 sm:mr-2" aria-hidden="true" />
          <span className="hidden sm:inline">Nova solicitação</span>
        </Button>
      </PageHeader>

      <GatingBanner />

      <FilterBar
        fields={campos}
        values={filtros}
        hasActiveFilters={temFiltro}
        onChange={(chave, valor) => {
          setPagina(1);
          setFiltros((atual) => ({ ...atual, [chave]: valor }));
        }}
        onClear={() => {
          setPagina(1);
          setFiltros({});
          setPeriodo({ from: '', to: '' });
        }}
      >
        <DateRangeFilter
          label="Criada em"
          from={periodo.from}
          to={periodo.to}
          onChange={(intervalo) => {
            setPagina(1);
            setPeriodo(intervalo);
          }}
        />
      </FilterBar>

      {isError ? (
        <ErrorState
          variant="load-failed"
          description="Não foi possível buscar as solicitações agora. Tente novamente em instantes."
          primaryAction={{ label: 'Tentar de novo', onClick: () => void refetch() }}
        />
      ) : (
        <DataTable
          columns={tableView.orderedColumns}
          personalizacao={tableView}
          data={data?.itens ?? []}
          isLoading={isLoading}
          total={data?.total}
          page={pagina}
          perPage={TAMANHO_DA_PAGINA}
          onPageChange={setPagina}
          onPerPageChange={() => {}}
          onRowClick={(item) => navigate(`/credenciado/solicitacoes/${item.id}`)}
          linhaComElementoInterativo
          emptyIcon={ClipboardList}
          emptyTitle="Nenhuma solicitação encontrada"
          emptyDescription={
            temFiltro
              ? 'Nenhuma solicitação atende a esses filtros. Tente afrouxar a busca.'
              : 'Você ainda não abriu nenhuma solicitação de serviço.'
          }
          emptyActionLabel={temFiltro ? undefined : 'Nova solicitação'}
          onEmptyAction={temFiltro ? undefined : () => navigate('/credenciado/solicitacoes/nova')}
          minWidth="720px"
          ariaLabel="Lista de solicitações de serviço"
        />
      )}
    </div>
  );
}

export default SolicitacoesPage;
